import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
    PostsFiltersDocument,
    PostsFiltersQuery,
    PostsFiltersQueryVariables,
} from '@/graphql/generated/posts-filters.generated';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { PostGroupBy, PostStage, PostsViewType, PostType } from '@/graphql/generated/types.generated';
import { PostsViewFieldsFragment } from '@/graphql/generated/posts-view.fragment.generated';

export const processPostsFilters = (searchParams: URLSearchParams): PostsFiltersQueryVariables => {
    const variables: PostsFiltersQueryVariables = {
        search: searchParams.get('search') || null,
        netProfileAuthIds: null,
        assignedToIds: null,
        labelsIds: null,
        stages: null,
        types: null,
        groupBy: null,
    };

    const profiles =
        searchParams
            .get('profiles')
            ?.split(',')
            .filter((v) => v) || [];
    if (profiles.length > 0) {
        variables.netProfileAuthIds = profiles;
    }

    const assignedTo =
        searchParams
            .get('assignedTo')
            ?.split(',')
            .filter((v) => v) || [];
    if (assignedTo.length > 0) {
        variables.assignedToIds = assignedTo;
    }

    const labels =
        searchParams
            .get('labels')
            ?.split(',')
            .filter((v) => v) || [];
    if (labels.length > 0) {
        variables.labelsIds = labels;
    }

    const stages =
        searchParams
            .get('stages')
            ?.split(',')
            .filter((v) => v) || [];
    if (stages.length > 0) {
        variables.stages = stages as PostStage[];
    }

    const types =
        searchParams
            .get('types')
            ?.split(',')
            .filter((v) => v) || [];
    if (types.length > 0) {
        variables.types = types as PostType[];
    }

    const groupByParam = searchParams.get('groupBy')?.trim();
    if (groupByParam && (groupByParam === PostGroupBy.Stage || groupByParam === PostGroupBy.Date)) {
        variables.groupBy = groupByParam === PostGroupBy.Stage ? PostGroupBy.Stage : PostGroupBy.Date;
    }

    return variables;
};

type PostsFiltersContextType = {
    filters: PostsFiltersQuery['postsFilters'];
    currentPostView: PostsViewFieldsFragment | null;
    loading: boolean;
    loadingView: boolean;
};

const PostsFiltersContext = createContext<PostsFiltersContextType | null>(null);

type PostsFiltersProviderProps = {
    publicView?: boolean;
    postsView?: PostsViewFieldsFragment;
    loadingView?: boolean;
};

export const PostsFiltersProvider = ({
    children,
    publicView,
    postsView,
    loadingView,
}: PropsWithChildren<PostsFiltersProviderProps>) => {
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { viewId } = useParams<{ viewId: string }>();

    const { t } = useTranslation();

    const initialVariables: PostsFiltersQueryVariables = useMemo(() => {
        let variables: PostsFiltersQueryVariables = {
            search: null,
            netProfileAuthIds: null,
            assignedToIds: null,
            labelsIds: null,
            stages: null,
            types: null,
            groupBy: null,
        };

        const isCalendarView = postsView?.viewType === PostsViewType.Calendar || viewId === 'calendar';
        const isKanbanView = postsView?.viewType === PostsViewType.Kanban || viewId === 'kanban';

        if (postsView) {
            variables = {
                search: postsView.query || null,
                netProfileAuthIds: null,
                assignedToIds: null,
                labelsIds: null,
                stages: postsView.stages && postsView.stages.length > 0 ? postsView.stages : null,
                types: postsView.types && postsView.types.length > 0 ? postsView.types : null,
                groupBy: postsView.groupBy || null,
            };

            if (!variables.groupBy && postsView.viewType === PostsViewType.Kanban) {
                variables.groupBy = PostGroupBy.Stage;
            }

            if (postsView.reviewers && postsView.reviewers.length > 0) {
                variables.assignedToIds = postsView.reviewers.map((r) => r.id);
            }

            if (postsView.labels && postsView.labels.length > 0) {
                variables.labelsIds = postsView.labels.map((l) => l.id);
            }

            if (postsView.profiles && postsView.profiles.length > 0) {
                variables.netProfileAuthIds = postsView.profiles.map((p) => p.id);
            }
        }

        // Override with URL params
        if (!publicView) {
            const changedFilters = processPostsFilters(searchParams);
            if (
                changedFilters.search ||
                (!changedFilters.search && searchParams.has('search') && postsView?.query)
            ) {
                variables.search = changedFilters.search;
            }
            if (
                changedFilters.netProfileAuthIds ||
                (!changedFilters.netProfileAuthIds &&
                    searchParams.has('profiles') &&
                    postsView?.profiles &&
                    postsView.profiles.length > 0)
            ) {
                variables.netProfileAuthIds = changedFilters.netProfileAuthIds;
            }
            if (
                changedFilters.assignedToIds ||
                (!changedFilters.assignedToIds &&
                    searchParams.has('assignedTo') &&
                    postsView?.reviewers &&
                    postsView.reviewers.length > 0)
            ) {
                variables.assignedToIds = changedFilters.assignedToIds;
            }
            if (
                changedFilters.labelsIds ||
                (!changedFilters.labelsIds &&
                    searchParams.has('labels') &&
                    postsView?.labels &&
                    postsView.labels.length > 0)
            ) {
                variables.labelsIds = changedFilters.labelsIds;
            }
            if (
                changedFilters.stages ||
                (!changedFilters.stages &&
                    searchParams.has('stages') &&
                    postsView?.stages &&
                    postsView.stages.length > 0)
            ) {
                variables.stages = changedFilters.stages;
            }
            if (
                changedFilters.types ||
                (!changedFilters.types &&
                    searchParams.has('types') &&
                    postsView?.types &&
                    postsView.types.length > 0)
            ) {
                variables.types = changedFilters.types;
            }

            if (
                changedFilters.groupBy ||
                (!changedFilters.groupBy && searchParams.has('groupBy') && postsView?.groupBy)
            ) {
                variables.groupBy = changedFilters.groupBy;
            }
            // Force Group by date if is calendar view, even if URL param is different or not present
            if (isCalendarView) {
                variables.groupBy = PostGroupBy.Date;
            }

            // Force Group by stage if is kanban view, even if URL param is different or not present
            if (isKanbanView) {
                variables.groupBy = PostGroupBy.Stage;
            }
        }

        return variables;
    }, [searchParams, publicView, postsView, viewId]);

    const { data, loading } = useQuery(PostsFiltersDocument, {
        variables: initialVariables,
        onError: (error) => {
            enqueueSnackbar(t(error.message));
        },
    });

    const postsFilters = useMemo(() => data?.postsFilters || {}, [data]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue: PostsFiltersContextType = useMemo(
        () => ({
            filters: postsFilters,
            loading,
            loadingView: loadingView || false,
            currentPostView: postsView || null,
        }),
        [postsFilters, postsView, loading, loadingView],
    );

    return <PostsFiltersContext.Provider value={contextValue}>{children}</PostsFiltersContext.Provider>;
};

export const usePostsFilters = () => {
    const context = useContext(PostsFiltersContext);
    if (!context) {
        throw new Error('usePostsFilters must be used within a PostsFiltersProvider');
    }
    return { filters: context.filters, loading: context.loading, loadingView: context.loadingView };
};

export const useCurrentPostView = () => {
    const context = useContext(PostsFiltersContext);
    if (!context) {
        throw new Error('useCurrentPostView must be used within a PostsFiltersProvider');
    }
    return { currentPostView: context.currentPostView };
};
